import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Gauge } from "lucide-react";
import { api } from "@/lib/firebase/api-client";
import { intervalleVisible } from "@/lib/utils/intervalle-visible";
import { useOrderStats } from "@/lib/hooks/queries";

/**
 * ===========================================================================
 *  PERFORMANCE DES FOURNISSEURS — la vue d'ensemble du patron (13/10/2026)
 * ===========================================================================
 *
 *  Une ligne par fournisseur : temps de preparation, CA realise (chaque
 *  article des commandes de la periode, au prix de vente, attribue a son
 *  fournisseur), part du catalogue en vente, ventes perdues, remboursements.
 *  Sur la journee, 7 jours ou 30 jours. Un clic sur une colonne la trie.
 *
 *  Le calcul et sa regle de cout vivent dans
 *  services/kpi-fournisseurs.service.ts ; le serveur le garde 30 minutes :
 *  l'ecran ne redemande donc rien plus souvent.
 * ===========================================================================
 */

type Jours = 1 | 7 | 30;
type Ligne = Awaited<ReturnType<typeof api.fournisseurs.kpi>>["lignes"][number];
type Colonne = "nom" | "prep" | "ca" | "catalogue" | "perdues" | "rembourse";

const PERIODES: Array<{ jours: Jours; libelle: string; filtre: string }> = [
  { jours: 1, libelle: "Aujourd'hui", filtre: "today" },
  { jours: 7, libelle: "7 jours", filtre: "7days" },
  { jours: 30, libelle: "30 jours", filtre: "30days" },
];

const dh = (centimes: number) =>
  `${(centimes / 100).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} DH`;

/** Vert / orange / rouge, seuils lus de gauche a droite. */
const teinte = (ok: boolean, moyen: boolean) =>
  ok ? "text-emerald-700" : moyen ? "text-amber-700" : "text-red-700";

export default function PerformanceFournisseurs({
  storeFilter,
  nomDuCompte,
  nomDuMagasin,
}: {
  storeFilter: string;
  nomDuCompte: (userId: string) => string;
  nomDuMagasin: (storeId: string | null) => string;
}) {
  const [jours, setJours] = useState<Jours>(1);
  const [donnees, setDonnees] = useState<Awaited<ReturnType<typeof api.fournisseurs.kpi>> | null>(null);
  const [chargement, setChargement] = useState(false);
  const [tri, setTri] = useState<{ col: Colonne; desc: boolean }>({ col: "ca", desc: true });
  // Le rattrapage de l'historique : un bouton, une fois (voir rattraperKpi).
  const [rattrapage, setRattrapage] = useState<"" | "encours" | string>("");
  const [version, setVersion] = useState(0);

  // LES TOTAUX SONT CEUX DE LA PAGE COMMANDES (13/10/2026) : meme requete,
  // meme cache, memes bornes. Le tableau en dessous les repartit par
  // fournisseur ; ce qui ne se rattache a personne s'affiche « non reparti ».
  const { data: stats } = useOrderStats({
    storeId: storeFilter !== "ALL" ? storeFilter : undefined,
    dateFilter: PERIODES.find((p) => p.jours === jours)!.filtre,
  });

  // Changer de periode vide l'ecran : afficher les chiffres de 7 jours sous
  // le bouton « Aujourd'hui » pendant le chargement, c'est mentir.
  useEffect(() => setDonnees(null), [jours]);

  useEffect(() => {
    let annule = false;
    const charger = () => {
      setChargement(true);
      api.fournisseurs
        .kpi(jours)
        .then((r) => !annule && setDonnees(r))
        .catch(() => {
          /* un confort : son absence ne casse pas le tableau de bord */
        })
        .finally(() => !annule && setChargement(false));
    };
    charger();
    const arret = intervalleVisible(charger, 30 * 60_000);
    return () => {
      annule = true;
      arret();
    };
  }, [jours, version]);

  async function rattraper() {
    setRattrapage("encours");
    try {
      const r = await api.fournisseurs.kpiRattrapage();
      setRattrapage(
        `Historique recalculé : ${r.paniers} paniers, ${r.magasins} magasin(s).`,
      );
      setVersion((v) => v + 1);
    } catch (e: any) {
      setRattrapage(`Échec : ${e?.message || "erreur inconnue"}`);
    }
  }

  // Le magasin choisi en haut de page, et seulement les fournisseurs qui ont
  // quelque chose a montrer sur la periode.
  const lignes = useMemo(() => {
    const brut = (donnees?.lignes || []).filter(
      (l) =>
        (storeFilter === "ALL" || l.storeId === storeFilter) &&
        (l.articles > 0 ||
          l.paniersPrepares > 0 ||
          l.articlesLivres > 0 ||
          l.articlesPerdus > 0 ||
          l.remboursements > 0),
    );
    const valeur = (l: Ligne): number | string => {
      switch (tri.col) {
        case "nom":
          return (l.nom || nomDuCompte(l.userId)).toLowerCase();
        case "prep":
          return l.prepMoyenneMin ?? -1;
        case "ca":
          return l.caCentimes;
        case "catalogue":
          return l.pctActifs ?? -1;
        case "perdues":
          return l.ventesPerduesCentimes;
        case "rembourse":
          return l.remboursementsCentimes;
      }
    };
    return [...brut].sort((a, b) => {
      const va = valeur(a);
      const vb = valeur(b);
      const ordre = va < vb ? -1 : va > vb ? 1 : 0;
      return tri.desc ? -ordre : ordre;
    });
  }, [donnees, storeFilter, tri, nomDuCompte]);

  const total = useMemo(() => {
    const t = { ca: 0, perdues: 0, rembourse: 0, prepMin: 0, paniers: 0, articles: 0, actifs: 0 };
    for (const l of lignes) {
      t.ca += l.caCentimes;
      t.perdues += l.ventesPerduesCentimes;
      t.rembourse += l.remboursementsCentimes;
      t.prepMin += (l.prepMoyenneMin ?? 0) * l.paniersPrepares;
      t.paniers += l.paniersPrepares;
      t.articles += l.articles;
      t.actifs += l.articlesActifs;
    }
    return t;
  }, [lignes]);

  // Les totaux de la page Commandes, et la part que le tableau n'attribue a
  // aucun fournisseur (frais, remises, commandes en cours, motifs magasin,
  // remboursements pas encore qualifies).
  const periode = (stats as any)?.period;
  const officiel = {
    ca: Number(periode?.revenue ?? total.ca),
    perdues: Number(periode?.lostSales ?? total.perdues),
    rembourse: Number(periode?.refunds ?? total.rembourse),
  };
  const nonReparti = {
    ca: Math.max(0, officiel.ca - total.ca),
    perdues: Math.max(0, officiel.perdues - total.perdues),
    rembourse: Math.max(0, officiel.rembourse - total.rembourse),
  };

  const entete = (col: Colonne, libelle: string, aide?: string) => (
    <th
      className="px-3 py-2.5 text-left font-semibold whitespace-nowrap cursor-pointer select-none hover:text-gray-900"
      onClick={() => setTri((t) => ({ col, desc: t.col === col ? !t.desc : col !== "nom" }))}
      title={aide}
    >
      <span className="inline-flex items-center gap-1">
        {libelle}
        {tri.col === col &&
          (tri.desc ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
      </span>
    </th>
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* En-tete : titre, periode */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-5 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <Gauge className="w-4 h-4 text-gray-500" />
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Performance des fournisseurs
          </p>
          {chargement && <span className="text-[11px] text-gray-400">mise à jour…</span>}
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          {PERIODES.map((p) => (
            <button
              key={p.jours}
              type="button"
              onClick={() => setJours(p.jours)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                jours === p.jours ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {p.libelle}
            </button>
          ))}
        </div>
      </div>

      {/* Les totaux de la periode, lisibles d'un coup d'oeil */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-100 border-y border-gray-100">
        {[
          { libelle: "Revenus (page Commandes)", valeur: dh(officiel.ca), ton: "text-gray-900" },
          {
            libelle: "Préparation moyenne des paniers",
            valeur: total.paniers ? `${Math.round(total.prepMin / total.paniers)} min` : "—",
            ton: "text-gray-900",
          },
          { libelle: "Ventes perdues", valeur: dh(officiel.perdues), ton: officiel.perdues ? "text-red-700" : "text-gray-900" },
          { libelle: "Remboursements", valeur: dh(officiel.rembourse), ton: officiel.rembourse ? "text-red-700" : "text-gray-900" },
        ].map((k) => (
          <div key={k.libelle} className="bg-white px-4 py-3">
            <p className="text-[11px] text-gray-500">{k.libelle}</p>
            <p className={`text-lg font-bold ${k.ton}`}>{k.valeur}</p>
          </div>
        ))}
      </div>

      {/* Le tableau */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              {entete("nom", "Fournisseur")}
              {entete("prep", "Préparation", "Arrivée du panier → « prêt », moyenne de la période")}
              {entete("ca", "CA réalisé", "Articles vendus sur la période, au prix de vente de la commande, attribués au fournisseur de chaque article")}
              {entete("catalogue", "Catalogue actif", "Articles en vente / ses articles — à la dernière synchro Glovo")}
              {entete("perdues", "Ventes perdues", "Articles retirés de la commande (import Glovo), répartis par fournisseur")}
              {entete("rembourse", "Remboursements", "Remboursements des commandes de la période, part imputée au fournisseur selon les motifs")}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {lignes.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  {donnees ? "Rien sur cette période." : "Chargement…"}
                </td>
              </tr>
            )}
            {lignes.map((l) => {
              const pct = l.pctActifs;
              return (
                <tr key={l.fournisseurId} className="hover:bg-gray-50/70">
                  <td className="px-3 py-2.5">
                    <p className="font-semibold text-gray-900">{(l.nom || nomDuCompte(l.userId))}</p>
                    {storeFilter === "ALL" && (
                      <p className="text-[11px] text-gray-400">{nomDuMagasin(l.storeId)}</p>
                    )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {l.prepMoyenneMin === null ? (
                      <span className="text-gray-300">—</span>
                    ) : (
                      <>
                        <span className={`font-bold ${teinte(l.prepMoyenneMin <= 10, l.prepMoyenneMin <= 20)}`}>
                          {l.prepMoyenneMin} min
                        </span>
                        <span className="text-[11px] text-gray-400"> · {l.paniersPrepares} panier{l.paniersPrepares > 1 ? "s" : ""}</span>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {l.articlesLivres ? (
                      <>
                        <span className="font-bold text-gray-900">{dh(l.caCentimes)}</span>
                        <span className="text-[11px] text-gray-400"> · {l.articlesLivres} art.</span>
                      </>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {pct === null ? (
                      <span className="text-gray-300">—</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${pct >= 90 ? "bg-emerald-500" : pct >= 70 ? "bg-amber-500" : "bg-red-500"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className={`font-bold ${teinte(pct >= 90, pct >= 70)}`}>{pct}%</span>
                        <span className="text-[11px] text-gray-400">
                          {l.articlesActifs}/{l.articles}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {l.articlesPerdus ? (
                      <>
                        <span className="font-bold text-red-700">{dh(l.ventesPerduesCentimes)}</span>
                        <span className="text-[11px] text-gray-400"> · {l.articlesPerdus} art.</span>
                      </>
                    ) : (
                      <span className="text-gray-300">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {l.remboursements ? (
                      <>
                        <span className="font-bold text-red-700">{dh(l.remboursementsCentimes)}</span>
                        <span className="text-[11px] text-gray-400"> · {l.remboursements} cmd</span>
                      </>
                    ) : (
                      <span className="text-gray-300">0</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {/* Ce que les totaux du haut contiennent et qu'aucun fournisseur ne
                porte : la ligne qui fait retomber chaque colonne juste. */}
            {donnees && lignes.length > 0 && (nonReparti.ca > 0 || nonReparti.perdues > 0 || nonReparti.rembourse > 0) && (
              <tr className="bg-gray-50/60 text-gray-500">
                <td className="px-3 py-2.5">
                  <p className="font-semibold">Non réparti</p>
                  <p className="text-[11px] text-gray-400">écarts de prix Glovo, remises, frais, non qualifié</p>
                </td>
                <td className="px-3 py-2.5" />
                <td className="px-3 py-2.5 whitespace-nowrap font-semibold">{nonReparti.ca ? dh(nonReparti.ca) : "0"}</td>
                <td className="px-3 py-2.5" />
                <td className="px-3 py-2.5 whitespace-nowrap font-semibold">{nonReparti.perdues ? dh(nonReparti.perdues) : "0"}</td>
                <td className="px-3 py-2.5 whitespace-nowrap font-semibold">{nonReparti.rembourse ? dh(nonReparti.rembourse) : "0"}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="px-4 md:px-5 py-2.5 text-[11px] text-gray-400 border-t border-gray-100">
        Catalogue actif : état à la synchro Glovo du{" "}
        {donnees?.catalogueLe
          ? new Date(donnees.catalogueLe).toLocaleString("fr-FR", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              timeZone: "Africa/Casablanca",
            })
          : "— (cliquer « Synchroniser Glovo »)"}
        .{" "}
        {rattrapage && rattrapage !== "encours" ? (
          <span className="text-gray-600">{rattrapage}</span>
        ) : (
          <button
            type="button"
            onClick={() => void rattraper()}
            disabled={rattrapage === "encours"}
            className="font-semibold text-gray-600 underline hover:text-gray-900 disabled:opacity-50"
            title="Remplit le temps de préparation (30 derniers jours) et le catalogue actif des magasins. Le relancer ne double rien."
          >
            {rattrapage === "encours" ? "Calcul de l'historique…" : "Recalculer depuis l'historique (30 jours)"}
          </button>
        )}
      </p>
    </div>
  );
}
